/**
 * REST client for the agent-memory listing API (`GET /agent-memory/{agentId}`
 * on the async HTTP API). Backs BOTH inspector memory views — the "Memory › All"
 * tiered list and the "Memory › Session" graph (via `useMemoryGraphSync`) — so
 * the two always agree: the agent's persisted memory records, grouped by tier.
 *
 * This is the only *reachable* memory-records source. The graph once hydrated
 * from a `/memory/events` SSE snapshot routed through the POST-only `/agents`
 * AgentCore proxy, which is unroutable in beta/prod (the graph stayed blank);
 * that path is retired in favor of this Lambda on the reachable async API. Same
 * Cognito access-token bearer auth as `sessionApi.ts`.
 *
 * `agentId` is the agent's slug — the same value the turn stream sends and the
 * runtime keys stored memory under (`memory/<sub>/<agentId>/...`).
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import type { StreamedMemoryRecord } from './agentStream.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

/** One module's stored records, grouped by tier. Mirrors the runtime's
 *  `MemorySection`; fields the console doesn't use (`asMessages`) are omitted.
 *  The shape the `/agent-memory` Lambda emits and both memory views consume. */
export interface MemorySnapshotSection {
  moduleId: string;
  header: string;
  records: readonly StreamedMemoryRecord[];
  trimmed: number;
}

/** Fetch the signed-in user's stored memory for an agent, grouped by tier.
 *  Returns `[]` when the store is empty. When `sessionId` is given, the result
 *  is scoped to that conversation (the server filters the `session` tier to it
 *  and excludes it from `episodic`, mirroring the runtime's recall semantics);
 *  omitting it returns every record across all sessions. */
export async function fetchAgentMemory(
  agentId: string,
  sessionId?: string,
): Promise<readonly MemorySnapshotSection[]> {
  const jwt = await getAccessToken();
  const query =
    sessionId !== undefined && sessionId.length > 0
      ? `?sessionId=${encodeURIComponent(sessionId)}`
      : '';
  const response = await fetch(
    `${asyncApiConfig.baseUrl}/agent-memory/${encodeURIComponent(agentId)}${query}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${jwt}` },
      signal: requestTimeout(),
    },
  );
  if (!response.ok) {
    console.error(`[agentMemoryApi] fetch failed (${response.status})`, await safeText(response));
    throw new Error('Could not load the agent memory.');
  }
  const body = (await response.json()) as { sections?: readonly MemorySnapshotSection[] };
  return body.sections ?? [];
}

/** Best-effort body read for an error log — never throws. */
async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}
